import { useEffect, useState, useCallback, useMemo } from "react";
import {
	Page,
	Layout,
	Card,
	Select,
	ChoiceList,
	TextField,
	Button,
	Banner,
	SkeletonPage,
	SkeletonBodyText,
	Checkbox,
	BlockStack,
	Box,
	InlineStack,
	Divider,
	Text,
} from "@shopify/polaris";
import { DeleteIcon } from "@shopify/polaris-icons";
import { useNavigate } from "react-router-dom";
import { authenticatedFetch } from "../api";
import { AppBridgeSaveBar } from "../components/AppBridgeSaveBar";
import { UpgradeLink } from "../components/UpgradeLink";
import type { PlanFeatureLimits, TranslationConfig } from "../types";

const ALL_ENGINES = [
	{ label: "DeepL", value: "deepl" },
	{ label: "OpenAI", value: "openai" },
];

/** Plan that first unlocks a given engine — used for the upgrade CTA copy. */
const ENGINE_UPGRADE_TARGET: Record<string, string> = {
	openai: "Growth",
};

interface ShopLocale {
	locale: string;
	name: string;
	primary: boolean;
	published: boolean;
}

interface GlossaryRow {
	source: string;
	target: string;
}

function glossaryFromConfig(
	termMapping?: Record<string, string>,
): GlossaryRow[] {
	if (!termMapping) return [];
	return Object.entries(termMapping).map(([source, target]) => ({
		source,
		target,
	}));
}

function glossaryToConfig(rows: GlossaryRow[]): Record<string, string> {
	return Object.fromEntries(
		rows
			.filter((r) => r.source.trim())
			.map((r) => [r.source.trim(), r.target.trim()]),
	);
}

export default function Configuration() {
	const navigate = useNavigate();
	const [config, setConfig] = useState<TranslationConfig | null>(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [saveSuccess, setSaveSuccess] = useState(false);
	const [isDirty, setIsDirty] = useState(false);

	const [engine, setEngine] = useState("deepl");
	const [sourceLocale, setSourceLocale] = useState("");
	const [targetLocales, setTargetLocales] = useState<string[]>([]);
	const [deeplGlossaryId, setDeeplGlossaryId] = useState("");
	const [extraInstructions, setExtraInstructions] = useState("");
	const [glossaryRows, setGlossaryRows] = useState<GlossaryRow[]>([]);
	const [ignoreCase, setIgnoreCase] = useState(false);
	const [fieldTitle, setFieldTitle] = useState(true);
	const [fieldBody, setFieldBody] = useState(true);
	const [fieldHandle, setFieldHandle] = useState(false);
	const [fieldMetaTitle, setFieldMetaTitle] = useState(false);
	const [fieldMetaDescription, setFieldMetaDescription] = useState(false);
	const [fieldMetafields, setFieldMetafields] = useState(false);
	const [fieldProductOptions, setFieldProductOptions] = useState(true);
	const [copyProductOptions, setCopyProductOptions] = useState(false);

	// Plan state (empty string == shop has no subscription yet)
	const [planName, setPlanName] = useState("");
	const [planLimits, setPlanLimits] = useState<PlanFeatureLimits | null>(null);

	// Shop locales from API
	const [shopLocales, setShopLocales] = useState<ShopLocale[]>([]);

	function applyConfig(res: TranslationConfig) {
		setConfig(res);
		setEngine(res.translation_engine ?? "deepl");
		setSourceLocale(res.source_locale ?? "");
		setTargetLocales(res.target_locales ?? []);
		setDeeplGlossaryId(res.deepl_glossary_id ?? "");
		setExtraInstructions((res.extra_instructions ?? []).join("\n"));
		setGlossaryRows(glossaryFromConfig(res.term_mapping));
		setIgnoreCase(res.mapping_ignore_case ?? false);
		setFieldTitle(res.fields?.title !== false);
		setFieldBody(res.fields?.body_html !== false);
		setFieldHandle(res.fields?.handle === true);
		setFieldMetaTitle(res.fields?.meta_title === true);
		setFieldMetaDescription(res.fields?.meta_description === true);
		setFieldMetafields(res.fields?.metafields === true);
		setFieldProductOptions(res.fields?.product_options !== false);
		setCopyProductOptions(res.to_copy?.product_options === true);
		setPlanName(res.planName ?? "");
		setPlanLimits(res.planFeatureLimits ?? null);
	}

	useEffect(() => {
		Promise.all([
			authenticatedFetch<TranslationConfig>("/api/config"),
			authenticatedFetch<{ locales: ShopLocale[] }>("/api/locales"),
		])
			.then(([configRes, localesRes]) => {
				applyConfig(configRes);
				setShopLocales(localesRes.locales ?? []);
				setLoading(false);
			})
			.catch((err: Error) => {
				setError(err.message);
				setLoading(false);
			});
	}, []);

	const markDirty = useCallback(() => {
		setIsDirty(true);
		setSaveSuccess(false);
	}, []);

	function handleEngineChange(value: string) { setEngine(value); markDirty(); }
	function handleSourceLocaleChange(value: string) { setSourceLocale(value); markDirty(); }
	function handleTargetLocalesChange(value: string[]) { setTargetLocales(value); markDirty(); }
	function handleDeeplGlossaryChange(value: string) { setDeeplGlossaryId(value); markDirty(); }
	function handleExtraInstructionsChange(value: string) { setExtraInstructions(value); markDirty(); }
	function handleIgnoreCaseChange(value: boolean) { setIgnoreCase(value); markDirty(); }

	function addGlossaryRow() {
		setGlossaryRows((prev) => [...prev, { source: "", target: "" }]);
		markDirty();
	}

	function removeGlossaryRow(index: number) {
		setGlossaryRows((prev) => prev.filter((_, i) => i !== index));
		markDirty();
	}

	function updateGlossaryRow(index: number, field: "source" | "target", value: string) {
		setGlossaryRows((prev) =>
			prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
		);
		markDirty();
	}

	async function handleSave() {
		setSaving(true);
		setSaveError(null);
		setSaveSuccess(false);
		try {
			await authenticatedFetch("/api/config", {
				method: "PUT",
				body: JSON.stringify({
					translation_engine: engine,
					source_locale: sourceLocale,
					target_locales: targetLocales,
					deepl_glossary_id: deeplGlossaryId || undefined,
					extra_instructions: extraInstructions
						? extraInstructions.split("\n").map((s) => s.trim()).filter(Boolean)
						: undefined,
					term_mapping: glossaryRows.length > 0 ? glossaryToConfig(glossaryRows) : undefined,
					mapping_ignore_case: ignoreCase,
					fields: {
						title: fieldTitle,
						body_html: fieldBody,
						handle: fieldHandle,
						meta_title: fieldMetaTitle,
						meta_description: fieldMetaDescription,
						metafields: fieldMetafields,
						product_options: fieldProductOptions,
					},
					to_copy: {
						product_options: copyProductOptions,
					},
				}),
			});
			setIsDirty(false);
			setSaveSuccess(true);
		} catch (err: unknown) {
			setSaveError(err instanceof Error ? err.message : "Save failed");
		} finally {
			setSaving(false);
		}
	}

	function handleDiscard() {
		if (config) applyConfig(config);
		setIsDirty(false);
		setSaveSuccess(false);
	}

	// Derive lock state from the shop's plan. When `planLimits` is null the shop
	// has no subscription yet — every control stays unlocked so the merchant can
	// pre-configure, and the top banner steers them to /plans before running.
	const engineOptions = useMemo(
		() =>
			ALL_ENGINES.map((opt) => ({
				...opt,
				disabled: planLimits ? !planLimits.engines.includes(opt.value) : false,
			})),
		[planLimits],
	);

	const lockedEngine =
		!!planLimits && !planLimits.engines.includes(engine)
			? ENGINE_UPGRADE_TARGET[engine]
			: null;

	const maxTargetLocales = planLimits?.target_locales ?? null;
	const localesAtLimit =
		maxTargetLocales !== null && targetLocales.length >= maxTargetLocales;

	const maxGlossary = planLimits?.glossary_entries ?? null;
	const glossaryAtLimit =
		maxGlossary !== null && glossaryRows.length >= maxGlossary;

	if (loading) {
		return (
			<SkeletonPage title="Configuration">
				<Layout>
					<Layout.Section>
						<Card><SkeletonBodyText lines={6} /></Card>
					</Layout.Section>
				</Layout>
			</SkeletonPage>
		);
	}

	return (
		<Page title="Configuration">
			<AppBridgeSaveBar id="config-save-bar" open={isDirty} discardConfirmation>
				<button variant="primary" loading={saving || undefined} onClick={handleSave} />
				<button onClick={handleDiscard} />
			</AppBridgeSaveBar>
			<Layout>
				{!planName && (
					<Layout.Section>
						<Banner
							tone="warning"
							title="Choose a plan to start translating"
							action={{ content: "View plans", onAction: () => navigate("/plans") }}
						>
							<p>
								You can pre-configure settings, but a plan is required before
								translations will run.
							</p>
						</Banner>
					</Layout.Section>
				)}

				{saveSuccess && (
					<Layout.Section>
						<Banner tone="success" title="Configuration saved" onDismiss={() => setSaveSuccess(false)} />
					</Layout.Section>
				)}

				{error && (
					<Layout.Section>
						<Banner tone="critical" title="Failed to load configuration">
							<p>{error}</p>
						</Banner>
					</Layout.Section>
				)}

				{saveError && (
					<Layout.Section>
						<Banner tone="critical" title="Failed to save" onDismiss={() => setSaveError(null)}>
							<p>{saveError}</p>
						</Banner>
					</Layout.Section>
				)}

				<Layout.AnnotatedSection
					title="Translation Engine"
					description="Choose which AI translation service to use."
				>
					<Card>
						<BlockStack gap="200">
							<Select
								label="Engine"
								options={engineOptions}
								value={engine}
								onChange={handleEngineChange}
								helpText={
									planName && planLimits && planLimits.engines.length === 1 ? (
										<span>
											The {planName} plan includes DeepL.{" "}
											<UpgradeLink unlocks="OpenAI" targetPlan="Growth" />
										</span>
									) : undefined
								}
							/>
							{lockedEngine && (
								<Banner tone="warning">
									<p>
										Your saved engine ({engine}) isn't available on the{" "}
										{planName} plan. Switch to DeepL or upgrade before saving.
									</p>
								</Banner>
							)}
						</BlockStack>
					</Card>
				</Layout.AnnotatedSection>

				<Layout.AnnotatedSection
					title="Locales"
					description="Set the source language and target languages."
				>
					<Card>
						<BlockStack gap="400">
							<Select
								label="Source locale"
								options={[
									{ label: "Select locale\u2026", value: "" },
									...shopLocales.map((l) => ({
										label: `${l.name} (${l.locale})${l.primary ? " — primary" : ""}`,
										value: l.locale,
									})),
								]}
								value={sourceLocale}
								onChange={handleSourceLocaleChange}
							/>
							<Divider />
							<ChoiceList
								allowMultiple
								title="Target locales"
								choices={shopLocales
									.filter((l) => l.locale !== sourceLocale)
									.map((l) => ({
										label: `${l.name} (${l.locale})`,
										value: l.locale,
										// Lock unchecked options once we hit the plan cap.
										disabled:
											localesAtLimit && !targetLocales.includes(l.locale),
									}))}
								selected={targetLocales}
								onChange={handleTargetLocalesChange}
							/>
							{planName && maxTargetLocales !== null && (
								<Text as="p" tone="subdued">
									Using {targetLocales.length} of {maxTargetLocales} target
									languages on the {planName} plan.{" "}
									{localesAtLimit && (
										<UpgradeLink unlocks="more languages" targetPlan="Growth" />
									)}
								</Text>
							)}
						</BlockStack>
					</Card>
				</Layout.AnnotatedSection>

				<Layout.AnnotatedSection
					title="Fields to Translate"
					description="Select which product fields should be translated."
				>
					<Card>
						<BlockStack gap="300">
							<Checkbox label="Title" checked={fieldTitle} onChange={(v) => { setFieldTitle(v); markDirty(); }} />
							<Checkbox label="Description (body_html)" checked={fieldBody} onChange={(v) => { setFieldBody(v); markDirty(); }} />
							<Checkbox label="Product handle" checked={fieldHandle} onChange={(v) => { setFieldHandle(v); markDirty(); }} />
							<Checkbox label="SEO title" checked={fieldMetaTitle} onChange={(v) => { setFieldMetaTitle(v); markDirty(); }} />
							<Checkbox label="SEO description" checked={fieldMetaDescription} onChange={(v) => { setFieldMetaDescription(v); markDirty(); }} />
							<Checkbox label="Metafields (all translatable product metafields)" checked={fieldMetafields} onChange={(v) => { setFieldMetafields(v); markDirty(); }} />
							<Checkbox
								label="Variant options (e.g. Color, Size, Red, Large)"
								checked={fieldProductOptions}
								onChange={(v) => { setFieldProductOptions(v); markDirty(); }}
								helpText="Translates option names and values so variant pickers display in the customer's language."
							/>
							{fieldProductOptions && (
								<Box paddingInlineStart="600">
									<Checkbox
										label="Copy options without translating"
										checked={copyProductOptions}
										onChange={(v) => { setCopyProductOptions(v); markDirty(); }}
										helpText="Register the source values verbatim in each target locale. Use this for brand-specific terms that must not be translated."
									/>
								</Box>
							)}
						</BlockStack>
					</Card>
				</Layout.AnnotatedSection>

				<Layout.AnnotatedSection
					title="Auto-Translate"
					description="Automatic translation rules have moved to their own page."
				>
					<Card>
						<BlockStack gap="300">
							<Text as="p" variant="bodyMd" tone="subdued">
								Configure which products get translated automatically on the dedicated Auto-Translate page.
							</Text>
							<div>
								<Button url="/auto-translate">Go to Auto-Translate</Button>
							</div>
						</BlockStack>
					</Card>
				</Layout.AnnotatedSection>

				<Layout.AnnotatedSection
					title="Glossary"
					description="Define term mappings to ensure specific words are always translated consistently. These are used by the translation engine to preserve brand names, technical terms, etc."
				>
					<Card>
						<BlockStack gap="400">
							<Checkbox
								label="Ignore case when matching source terms"
								checked={ignoreCase}
								onChange={handleIgnoreCaseChange}
							/>
							{glossaryRows.length > 0 && (
								<BlockStack gap="200">
									<InlineStack gap="400" align="start">
										<div style={{ flex: 1, fontWeight: 600, fontSize: "13px", color: "#6d7175" }}>
											Source term
										</div>
										<div style={{ flex: 1, fontWeight: 600, fontSize: "13px", color: "#6d7175" }}>
											Translation
										</div>
										<div style={{ width: "36px" }} />
									</InlineStack>
									{glossaryRows.map((row, index) => (
										<InlineStack key={index} gap="400" align="start" blockAlign="center">
											<div style={{ flex: 1 }}>
												<TextField
													label="Source term"
													labelHidden
													value={row.source}
													onChange={(v) => updateGlossaryRow(index, "source", v)}
													autoComplete="off"
													placeholder="e.g. Duxly"
												/>
											</div>
											<div style={{ flex: 1 }}>
												<TextField
													label="Translation"
													labelHidden
													value={row.target}
													onChange={(v) => updateGlossaryRow(index, "target", v)}
													autoComplete="off"
													placeholder="e.g. Duxly"
												/>
											</div>
											<Button
												variant="plain"
												tone="critical"
												onClick={() => removeGlossaryRow(index)}
												icon={DeleteIcon}
												accessibilityLabel="Remove term"
											/>
										</InlineStack>
									))}
								</BlockStack>
							)}
							{glossaryRows.length === 0 && (
								<Text as="p" tone="subdued">
									No glossary terms defined. Add terms to ensure consistent translation of brand names, product names, or technical terms.
								</Text>
							)}
							<div>
								<Button onClick={addGlossaryRow} disabled={glossaryAtLimit}>
									Add term
								</Button>
							</div>
							{planName && maxGlossary !== null && (
								<Text as="p" tone="subdued">
									Using {glossaryRows.length} of {maxGlossary} glossary terms on
									the {planName} plan.{" "}
									{glossaryAtLimit && (
										<UpgradeLink
											unlocks="more glossary terms"
											targetPlan="Growth"
										/>
									)}
								</Text>
							)}
						</BlockStack>
					</Card>
				</Layout.AnnotatedSection>

				{engine === "deepl" && (
					<Layout.AnnotatedSection
						title="DeepL Glossary"
						description="Optionally provide a DeepL glossary ID to enforce term consistency via DeepL's native glossary feature."
					>
						<Card>
							<TextField
								label="DeepL Glossary ID"
								value={deeplGlossaryId}
								onChange={handleDeeplGlossaryChange}
								autoComplete="off"
								placeholder="e.g. a1b2c3d4-..."
							/>
						</Card>
					</Layout.AnnotatedSection>
				)}

				{engine === "openai" && (
					<Layout.AnnotatedSection
						title="Extra Instructions"
						description="Additional instructions for OpenAI's translation prompt (one per line)."
					>
						<Card>
							<TextField
								label="Extra instructions"
								labelHidden
								value={extraInstructions}
								onChange={handleExtraInstructionsChange}
								multiline={4}
								autoComplete="off"
								placeholder="e.g. Keep brand names in English."
							/>
						</Card>
					</Layout.AnnotatedSection>
				)}

			</Layout>
		</Page>
	);
}
